import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Loader2, MonitorPlay, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { StatusBadge } from '@/components/StatusBadge';
import { DesktopOnly } from '@/components/dashboard/DesktopOnly';
import { LogDeviceTicketDialog } from '@/components/rmm/LogDeviceTicketDialog';
import { useRmmDevice, useRmmDeviceSoftware, type RmmAlert, type RmmAudit, type RmmDevice } from '@/api/rmm';
import { fmtDate, fmtRelative } from '@/lib/cobalt-helpers';
import { alertPriorityKind, avStatusMeta, daysSince, deviceClientName, fmtBytes, patchStatusMeta } from '@/lib/rmm-helpers';
import { ROUTES, rmmSitePath, ticketPath } from '@/lib/routes';
import { cn } from '@/lib/utils';
import { IntuneDeviceCard } from '@/components/cipp/IntuneDeviceCard';
import { ITGlueDeviceCard } from '@/components/docs/ITGlueCards';
import { HuntressDeviceCard } from '@/components/soc/HuntressCards';
import { OrbitLink } from '@/components/insights/OrbitLink';

const TAB_CLASS = 'data-[state=active]:border-b-2 data-[state=active]:border-brand';
const SECTION = 'rounded-lg border border-border bg-card p-5 shadow-sm';
const SECTION_TITLE = 'mb-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground';

export default function RmmDeviceDetailPage() {
  const { uid } = useParams();
  const detail = useRmmDevice(uid);
  const [tab, setTab] = useState('hardware');
  const [logTicketOpen, setLogTicketOpen] = useState(false);
  const [logTicketHover, setLogTicketHover] = useState(false);
  const software = useRmmDeviceSoftware(uid, tab === 'software');

  const body = (() => {
    if (detail.isLoading) {
      return (
        <div className="flex items-center justify-center gap-2 py-20 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading device…
        </div>
      );
    }
    if (detail.error) {
      return (
        <div className="mt-4 rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          Failed to load device: {detail.error instanceof Error ? detail.error.message : 'Unknown error'}
        </div>
      );
    }
    if (!detail.data) return <div className="py-8 text-sm">Device not found.</div>;

    const { device: d, alerts, audit, udfs } = detail.data;
    return (
      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-[1fr_320px] md:items-start">
        <div className="space-y-4">
          <HeaderCard d={d} />
          <AlertsSection alerts={alerts} />

          <section className={SECTION}>
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="border-b border-border bg-transparent p-0">
                <TabsTrigger value="hardware" className={TAB_CLASS}>Hardware</TabsTrigger>
                <TabsTrigger value="software" className={TAB_CLASS}>Software</TabsTrigger>
                <TabsTrigger value="fields" className={TAB_CLASS}>Custom fields{udfs.length > 0 && ` (${udfs.length})`}</TabsTrigger>
              </TabsList>
              <TabsContent value="hardware" className="pt-4">
                {audit ? <HardwareTab audit={audit} /> : (
                  <p className="text-sm text-muted-foreground">
                    No hardware audit available{d.deviceClass !== 'device' ? ' for this device type' : ''}.
                  </p>
                )}
              </TabsContent>
              <TabsContent value="software" className="pt-4">
                <SoftwareTab loading={software.isLoading} error={software.error} items={software.data?.items ?? []} />
              </TabsContent>
              <TabsContent value="fields" className="pt-4">
                {udfs.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No custom fields set on this device.</p>
                ) : (
                  <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                    {udfs.map((u) => (
                      <div key={u.key} className="min-w-0">
                        <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{u.label}</div>
                        <div className="mt-0.5 break-words">
                          {/^https?:\/\//i.test(u.value) ? (
                            <a href={u.value} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-brand-strong hover:underline dark:text-brand">
                              Open <ExternalLink className="h-3 w-3" />
                            </a>
                          ) : u.value}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </section>
        </div>

        <SideRail d={d} />
      </div>
    );
  })();

  const d = detail.data?.device;
  return (
    <DesktopOnly
      title="Devices are available on desktop"
      message="Open Cosmos on a larger screen to view RMM devices. The mobile app focuses on tickets and opportunities."
    >
      <div className="mx-auto w-full max-w-[1400px] px-4 py-4 md:px-6">
        <div className="flex items-center justify-between gap-2">
          <Button asChild variant="ghost" size="sm" className="-ml-2.5 text-muted-foreground hover:text-foreground">
            <Link to={ROUTES.rmm.devices}><ArrowLeft className="h-4 w-4" /> Devices</Link>
          </Button>
          {d && (
            <div className="flex items-center gap-2">
              {d.webRemoteUrl && (
                <Button asChild variant="outline" size="sm">
                  <a href={d.webRemoteUrl} target="_blank" rel="noreferrer"><MonitorPlay className="mr-1 h-3.5 w-3.5" /> Web Remote</a>
                </Button>
              )}
              <Button asChild variant="outline" size="sm">
                <a href={d.portalUrl} target="_blank" rel="noreferrer"><ExternalLink className="mr-1 h-3.5 w-3.5" /> Open in Datto RMM</a>
              </Button>
              <Button size="sm" onClick={() => setLogTicketOpen(true)} onMouseEnter={() => setLogTicketHover(true)} onFocus={() => setLogTicketHover(true)}>
                <Plus className="mr-1 h-3.5 w-3.5" /> Log ticket
              </Button>
            </div>
          )}
        </div>
        {body}
        {detail.data && (
          <LogDeviceTicketDialog open={logTicketOpen} onOpenChange={setLogTicketOpen} detail={detail.data} prefetch={logTicketHover} />
        )}
      </div>
    </DesktopOnly>
  );
}

// ---- Header ----

function HeaderCard({ d }: { d: RmmDevice }) {
  const client = deviceClientName(d);
  return (
    <section className={SECTION}>
      <div className="flex flex-wrap items-center gap-2">
        {d.online ? (
          <StatusBadge kind="success" dot>Online</StatusBadge>
        ) : (
          <StatusBadge kind={(daysSince(d.lastSeen) ?? 999) >= 30 ? 'warning' : 'neutral'} dot>
            Offline · last seen {fmtRelative(d.lastSeen)}
          </StatusBadge>
        )}
        {d.category && <StatusBadge>{d.type && d.type !== d.category ? `${d.category} · ${d.type}` : d.category}</StatusBadge>}
        {d.rebootRequired && <StatusBadge kind="warning">Reboot required</StatusBadge>}
        {d.suspended && <StatusBadge kind="warning">Suspended</StatusBadge>}
      </div>
      <h1 className="mt-3 text-[22px] font-[650] tracking-[-0.018em] text-foreground">{d.hostname}</h1>
      {d.description && d.description !== d.hostname && (
        <p className="mt-0.5 text-sm text-muted-foreground">{d.description}</p>
      )}
      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
        <span>
          <span className="text-muted-foreground">Client </span>
          <OrbitLink companyId={d.autotaskCompanyId}>{client}</OrbitLink>
          {d.autotaskCompanyId == null && (
            <span className="ml-1 text-xs text-warning" title={`Datto site "${d.siteName}" isn't linked to an Autotask company`}>· unlinked</span>
          )}
        </span>
        <span>
          <span className="text-muted-foreground">Site </span>
          <Link to={rmmSitePath(d.siteUid)} className="hover:underline" title={`All devices at ${d.siteName}`}>{d.siteName}</Link>
        </span>
        {d.lastLoggedInUser && <span><span className="text-muted-foreground">Last user </span>{d.lastLoggedInUser}</span>}
      </div>
    </section>
  );
}

// ---- Alerts ----

function AlertsSection({ alerts }: { alerts: RmmAlert[] }) {
  return (
    <section className={SECTION}>
      <div className={SECTION_TITLE}>Open alerts ({alerts.length})</div>
      {alerts.length === 0 ? (
        <p className="text-sm text-muted-foreground">No open alerts.</p>
      ) : (
        <ul className="divide-y divide-divider">
          {alerts.map((a) => (
            <li key={a.alertUid} className="py-2.5 first:pt-0 last:pb-0">
              <div className="flex items-start gap-3">
                <StatusBadge kind={alertPriorityKind(a.priority)} className="mt-0.5 shrink-0">{a.priority}</StatusBadge>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-foreground">{a.summary}</div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                    <span>{a.kind}</span>
                    <span>·</span>
                    <span title={new Date(a.timestamp).toLocaleString()}>{fmtRelative(a.timestamp)}</span>
                    {a.muted && <><span>·</span><span>Muted</span></>}
                    {a.ticketNumber && (
                      <>
                        <span>·</span>
                        {a.ticketId != null ? (
                          <Link to={ticketPath(a.ticketId)} className="font-medium text-brand-strong hover:underline dark:text-brand">
                            Ticket {a.ticketNumber}
                          </Link>
                        ) : (
                          <span className="line-through" title="This ticket no longer exists in Autotask (deleted or merged)">Ticket {a.ticketNumber}</span>
                        )}
                      </>
                    )}
                  </div>
                  {a.diagnostics && (
                    <details className="mt-1.5">
                      <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">Diagnostics</summary>
                      <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap rounded bg-muted/50 p-2 font-mono text-[11px] text-foreground">
                        {a.diagnostics}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---- Hardware ----

function HardwareTab({ audit: a }: { audit: RmmAudit }) {
  return (
    <div className="space-y-5 text-sm">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Manufacturer">{a.manufacturer}</Field>
        <Field label="Model">{a.model}</Field>
        <Field label="Serial number" mono>{a.serialNumber}</Field>
        <Field label="Processor">{a.processors.join(', ') || null}</Field>
        <Field label="CPU cores">{a.cpuCores}</Field>
        <Field label="Memory">
          {a.memoryBytes != null ? fmtBytes(a.memoryBytes) : null}
          {a.memoryModules.length > 0 && (
            <span className="text-muted-foreground"> · {a.memoryModules.length} module{a.memoryModules.length === 1 ? '' : 's'}</span>
          )}
        </Field>
        <Field label="BIOS">{a.biosVersion}{a.biosReleaseDate && <span className="text-muted-foreground"> · {fmtDate(a.biosReleaseDate)}</span>}</Field>
        <Field label="Baseboard">{a.baseBoard}</Field>
        <Field label="Graphics">{a.videoAdapters.join(', ') || null}</Field>
      </div>

      {a.disks.length > 0 && (
        <div>
          <div className={SECTION_TITLE}>Disks</div>
          <div className="space-y-2">
            {a.disks.map((disk, i) => {
              const used = disk.size && disk.free != null ? 1 - disk.free / disk.size : null;
              return (
                <div key={`${disk.id}-${i}`} className="flex items-center gap-3">
                  <span className="w-10 shrink-0 font-mono text-xs">{disk.id ?? '?'}</span>
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                    {used != null && (
                      <div
                        className={cn('h-full rounded-full', used >= 0.9 ? 'bg-destructive' : used >= 0.8 ? 'bg-warning' : 'bg-brand')}
                        style={{ width: `${Math.min(100, Math.max(0, used * 100))}%` }}
                      />
                    )}
                  </div>
                  <span className="w-48 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                    {fmtBytes(disk.free)} free of {fmtBytes(disk.size)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {a.nics.length > 0 && (
        <div>
          <div className={SECTION_TITLE}>Network adapters</div>
          <table className="w-full text-xs">
            <thead className="text-left text-muted-foreground">
              <tr><th className="pb-1 font-medium">Adapter</th><th className="pb-1 font-medium">IPv4</th><th className="pb-1 font-medium">MAC</th></tr>
            </thead>
            <tbody className="divide-y divide-divider">
              {a.nics.map((n, i) => (
                <tr key={`${n.mac}-${i}`}>
                  <td className="py-1.5 pr-3">{n.name ?? '—'}</td>
                  <td className="py-1.5 pr-3 font-mono">{n.ipv4 ?? '—'}</td>
                  <td className="py-1.5 font-mono">{n.mac ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Field({ label, children, mono }: { label: string; children: React.ReactNode; mono?: boolean }) {
  const empty = children == null || children === '' || (Array.isArray(children) && children.every((c) => c == null || c === false));
  return (
    <div className="min-w-0">
      <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={cn('mt-0.5 break-words', mono && 'font-mono text-xs')}>{empty ? '—' : children}</div>
    </div>
  );
}

// ---- Software ----

function SoftwareTab({ loading, error, items }: { loading: boolean; error: unknown; items: { name: string; version: string | null }[] }) {
  const [q, setQ] = useState('');
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? items.filter((i) => i.name.toLowerCase().includes(s)) : items;
  }, [items, q]);

  if (loading) {
    return <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading software…</div>;
  }
  if (error) {
    return <p className="text-sm text-destructive">Failed to load software: {error instanceof Error ? error.message : 'Unknown error'}</p>;
  }
  if (items.length === 0) return <p className="text-sm text-muted-foreground">No software reported for this device.</p>;

  return (
    <div>
      <div className="mb-3 flex items-center gap-3">
        <SearchInput withIcon placeholder="Filter software" className="w-64" value={q} onValueChange={setQ} />
        <span className="text-xs text-muted-foreground">{shown.length} of {items.length}</span>
      </div>
      <div className="max-h-[480px] overflow-auto rounded-md border">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-muted text-left text-muted-foreground">
            <tr><th className="px-3 py-1.5 font-medium">Name</th><th className="px-3 py-1.5 font-medium">Version</th></tr>
          </thead>
          <tbody className="divide-y divide-divider">
            {shown.map((s, i) => (
              <tr key={`${s.name}-${i}`}>
                <td className="px-3 py-1.5">{s.name}</td>
                <td className="px-3 py-1.5 font-mono">{s.version ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---- Side rail ----

function SideRail({ d }: { d: RmmDevice }) {
  const patch = patchStatusMeta(d.patchStatus);
  const av = avStatusMeta(d.antivirusStatus);
  return (
    <div className="space-y-4">
      <section className="rounded-lg border border-border bg-card shadow-sm">
        <div className="px-4 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Health</div>
        <PropRow label="Patching">
          {patch ? <StatusBadge kind={patch.kind}>{patch.label}</StatusBadge> : '—'}
          {(d.patchesApprovedPending || d.patchesNotApproved || d.patchesInstalled) ? (
            <div className="mt-1 text-xs text-muted-foreground">
              {d.patchesApprovedPending ?? 0} pending · {d.patchesNotApproved ?? 0} not approved · {d.patchesInstalled ?? 0} installed
            </div>
          ) : null}
        </PropRow>
        <PropRow label="Antivirus">
          {av ? <StatusBadge kind={av.kind}>{av.label}</StatusBadge> : '—'}
          {d.antivirusProduct && <div className="mt-1 text-xs text-muted-foreground">{d.antivirusProduct}</div>}
        </PropRow>
        <PropRow label="Software">{d.softwareStatus ?? '—'}</PropRow>
        <PropRow label="Open alerts">{d.openAlerts}</PropRow>
      </section>

      <IntuneDeviceCard uid={d.uid} />

      <ITGlueDeviceCard uid={d.uid} />
      <HuntressDeviceCard uid={d.uid} />

      <section className="rounded-lg border border-border bg-card shadow-sm">
        <div className="px-4 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">System</div>
        <PropRow label="OS">{d.operatingSystem ?? '—'}{d.a64Bit && <span className="text-muted-foreground"> · 64-bit</span>}</PropRow>
        <PropRow label="Domain">{d.domain ?? '—'}</PropRow>
        <PropRow label="Internal IP"><span className="font-mono text-xs">{d.intIpAddress ?? '—'}</span></PropRow>
        <PropRow label="External IP"><span className="font-mono text-xs">{d.extIpAddress ?? '—'}</span></PropRow>
        <PropRow label="Agent"><span className="font-mono text-xs">{d.agentVersion ?? '—'}</span></PropRow>
        <PropRow label="Warranty">{d.warrantyDate ? fmtDate(d.warrantyDate) : '—'}</PropRow>
      </section>

      <section className="rounded-lg border border-border bg-card shadow-sm">
        <div className="px-4 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Activity</div>
        <PropRow label="Last seen">{d.online ? 'Now' : <Ts iso={d.lastSeen} />}</PropRow>
        <PropRow label="Last reboot"><Ts iso={d.lastReboot} /></PropRow>
        <PropRow label="Last audit"><Ts iso={d.lastAuditDate} /></PropRow>
        <PropRow label="Added"><Ts iso={d.creationDate} /></PropRow>
      </section>
    </div>
  );
}

function Ts({ iso }: { iso: string | null }) {
  if (!iso) return <>—</>;
  return <span title={new Date(iso).toLocaleString()}>{fmtRelative(iso)}</span>;
}

function PropRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 border-t border-divider px-4 py-2.5 first:border-t-0">
      <span className="shrink-0 pt-[2px] text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
      <div className="min-w-0 text-right text-[13px] text-foreground">{children}</div>
    </div>
  );
}
